/*
  Product page buy form.

  Wraps the add to cart form written by kv-buy-buttons.liquid. The form is
  posted by the cart drawer script (kv-cart-behavior), which adds the chosen
  quantity once, writes the cart's reason next to the form when the cart
  refuses and opens the drawer when it accepts. This element only does the
  part of the theme's product-form element that the rest of the product page
  still relies on, since that element is not used here:

  - When an option is picked the theme's product-info element fetches the new
    variant. Meanwhile the button is switched off and an earlier cart error is
    cleared.
  - Once the variant has arrived, the button follows the button of the freshly
    rendered section: on and reading "Add to cart", or off and reading "Sold
    out".
  - A combination of options that makes no variant leaves the variant input
    empty; the button then reads "Unavailable".
  - After every change to the cart the quantity rules are read again, so the
    count already in the cart and the most that can still be added stay true.
  - A number larger than the most that can still be added never reaches the
    cart: the form stops there and the reason is written next to the number
    box instead. The most that can be added is the quantity box's own upper
    limit, which the theme keeps in step with the stock left, the product's
    order limit and what is already in the cart.

  The words are the theme's own variant strings, plus the one sentence next to
  the number box, which the quantity block hands over in its holder. The
  theme's product-info element keeps doing everything else on a variant
  change: the variant input, price, pictures, stock line and quantity rules.
*/
if (!customElements.get('kv-product-form')) {
  customElements.define(
    'kv-product-form',
    class KvProductForm extends HTMLElement {
      connectedCallback() {
        this.form = this.querySelector('form');
        if (!this.form) return;

        this.button = this.form.querySelector('[name="add"]');
        this.label = this.button ? this.button.querySelector('span') : null;
        this.errorBox = this.querySelector('[data-kv-cart-add-error]');
        this.productInfo = this.closest('product-info');

        this.unsubscribers = [];
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers.push(
            subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => this.onOptionChange(event)),
            subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event)),
            // The theme announces every fresh set of limits it works out for
            // the number box; each one is read back here, since the box has to
            // be locked whenever there is nothing left to add.
            subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.applyStockLock())
          );
        }
        this.applyStockLock();

        this.onVariantInput = (event) => {
          if (event.target.name === 'id' && event.target.value === '') {
            this.setButton(true, this.strings.unavailable);
          }
        };
        this.form.addEventListener('change', this.onVariantInput);

        // Caught on the way down, before the cart drawer script sees the form
        // on the way up, so a number over the limit never becomes a request.
        this.onSubmit = (event) => {
          if (this.quantityWithinLimit()) return;
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.showQuantityError();
          const input = this.quantityInput;
          if (input) {
            input.focus();
            input.select();
          }
        };
        this.form.addEventListener('submit', this.onSubmit, true);

        // While the number is being typed the message follows it, so the
        // shopper sees the limit before reaching for the button. The number
        // box sits outside the form, which its form attribute ties it to, so
        // its events are watched from the document rather than from the form.
        // Typing raises input; the - and + buttons change the number without
        // one and raise change instead, so both are watched.
        this.onQuantityInput = (event) => {
          if (event.target !== this.quantityInput) return;
          if (this.quantityWithinLimit()) {
            this.clearQuantityError();
          } else {
            this.showQuantityError();
          }
        };
        document.addEventListener('input', this.onQuantityInput, true);
        document.addEventListener('change', this.onQuantityInput, true);

        this.onCartUpdated = () => this.refreshQuantityRules();
        document.addEventListener('kv-cart:updated', this.onCartUpdated);
      }

      disconnectedCallback() {
        (this.unsubscribers || []).forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
        if (this.form && this.onVariantInput) this.form.removeEventListener('change', this.onVariantInput);
        if (this.form && this.onSubmit) this.form.removeEventListener('submit', this.onSubmit, true);
        if (this.onQuantityInput) {
          document.removeEventListener('input', this.onQuantityInput, true);
          document.removeEventListener('change', this.onQuantityInput, true);
        }
        if (this.onCartUpdated) document.removeEventListener('kv-cart:updated', this.onCartUpdated);
      }

      get strings() {
        return window.variantStrings || {};
      }

      // The number box belongs to the quantity block, which sits outside this
      // form and is tied to it by its form attribute, so the form's own list
      // of fields is where to find it.
      get quantityInput() {
        if (!this.form || !this.form.elements) return null;
        const field = this.form.elements.namedItem('quantity');
        return field && field.tagName === 'INPUT' ? field : null;
      }

      get quantityErrorBox() {
        return document.getElementById(`Quantity-Error-${this.dataset.sectionId}`);
      }

      // The most that can still be added: the box's own upper limit - the
      // stock left or the product's order limit, whichever is smaller - less
      // what is already in the cart. The theme drops the upper limit from the
      // box once nothing is left to add, which would read as "no limit", so
      // the two numbers it keeps beside the box are what is counted here.
      // null means the shop set no limit at all.
      get quantityLimit() {
        const input = this.quantityInput;
        if (!input) return null;
        const allowed = parseInt(input.dataset.max, 10);
        if (Number.isFinite(allowed)) {
          const inCart = parseInt(input.dataset.cartQuantity, 10);
          return Math.max(allowed - (Number.isFinite(inCart) ? inCart : 0), 0);
        }
        const attribute = parseInt(input.max, 10);
        return Number.isFinite(attribute) ? attribute : null;
      }

      get quantityPlusButton() {
        const input = this.quantityInput;
        const box = input ? input.closest('quantity-input') : null;
        return box ? box.querySelector('[name="plus"]') : null;
      }

      // Nothing left to add - the cart already holds the whole stock, or the
      // whole of what one order may take. The theme answers that by setting
      // the box to zero and taking its upper limit away, which leaves a box
      // reading 0 whose plus button still counts up. Instead the box is held
      // at its smallest number and everything that could add another one is
      // switched off, with the reason written underneath. Picking another
      // variant brings its own limits and opens all of it again.
      applyStockLock() {
        const input = this.quantityInput;
        if (!input) return;
        const locked = this.quantityLimit === 0;
        const plus = this.quantityPlusButton;

        if (locked) {
          const smallest = input.dataset.min || '1';
          if (input.min !== smallest) input.min = smallest;
          if (input.value !== smallest) input.value = smallest;
          input.max = smallest;
          if (plus) {
            plus.disabled = true;
            plus.classList.add('disabled');
          }
          if (this.button && !this.button.hasAttribute('disabled')) {
            this.button.setAttribute('disabled', '');
            this.stockLockedButton = true;
          }
          this.showQuantityError();
          return;
        }

        if (plus && plus.disabled) {
          plus.disabled = false;
          plus.classList.toggle('disabled', parseInt(input.value, 10) >= parseInt(input.max, 10));
        }
        if (this.stockLockedButton) {
          this.stockLockedButton = false;
          if (this.button) this.button.removeAttribute('disabled');
        }
        if (this.quantityWithinLimit()) this.clearQuantityError();
      }

      quantityWithinLimit() {
        const input = this.quantityInput;
        if (!input) return true;
        const limit = this.quantityLimit;
        if (limit === null) return true;
        // Nothing left to add: the number in the box no longer matters, since
        // an empty or zero box is sent to the cart as one.
        if (limit === 0) return false;
        const value = parseInt(input.value, 10);
        if (!Number.isFinite(value)) return true;
        return value <= limit;
      }

      showQuantityError() {
        const box = this.quantityErrorBox;
        const input = this.quantityInput;
        if (!box || !input) return;
        const limit = this.quantityLimit;
        // Nothing left to add reads differently from a limit that still has
        // room under it, so the block hands over both sentences.
        const template = (limit === 0 ? box.dataset.messageNone : box.dataset.message) || box.dataset.message || '';
        box.textContent = template.replace('[count]', limit === null ? '' : limit);
        box.hidden = false;
        input.setAttribute('aria-invalid', 'true');
        input.setAttribute('aria-describedby', box.id);
      }

      clearQuantityError() {
        const box = this.quantityErrorBox;
        if (box) {
          box.textContent = '';
          box.hidden = true;
        }
        const input = this.quantityInput;
        if (input) {
          input.removeAttribute('aria-invalid');
          input.removeAttribute('aria-describedby');
        }
      }

      onOptionChange({ data } = {}) {
        const target = data && data.event && data.event.target;
        if (!this.productInfo || !target || !this.productInfo.contains(target)) return;
        this.setButton(true);
        this.clearError();
        this.clearQuantityError();
      }

      onVariantChange({ data } = {}) {
        if (!data || !data.html || data.sectionId !== this.dataset.sectionId) return;
        const fresh = data.html.getElementById(`ProductSubmitButton-${data.sectionId}`);
        const disabled = fresh ? fresh.hasAttribute('disabled') : true;
        this.setButton(disabled, disabled ? this.strings.soldOut : this.strings.addToCart);
        // The theme works the number box's limits out from the values it is
        // still holding and takes in the new variant's only afterwards, which
        // would leave the box carrying the limit of the variant before it.
        // This runs once the new values are in place, by which time the theme
        // has finished its own pass.
        // Only when there is a number box to work out limits for: a merchant
        // may have taken the quantity block off the page altogether.
        const info = this.productInfo;
        if (info && info.quantityInput && typeof info.setQuantityBoundries === 'function') {
          info.setQuantityBoundries();
        }
        // The new variant brings its own limit and the box is set back to its
        // smallest number, so any message about the old one is stale.
        this.clearQuantityError();
      }

      setButton(disabled, text) {
        if (!this.button) return;
        this.button.toggleAttribute('disabled', disabled);
        if (this.label && text) this.label.textContent = text;
      }

      clearError() {
        if (!this.errorBox) return;
        const text = this.errorBox.querySelector('[data-kv-cart-add-error-text]');
        if (text) text.textContent = '';
        this.errorBox.hidden = true;
      }

      // The same request the theme's product-info element makes after an add
      // from its own product form, whose answer it applies with its own method.
      refreshQuantityRules() {
        const info = this.productInfo;
        if (!info || !info.quantityForm || typeof info.updateQuantityRules !== 'function') return;
        if (info.dataset.originalSection) return;

        const variantInput = this.form.querySelector('input[name="id"]');
        const variantId = variantInput && variantInput.value;
        if (!variantId) return;

        const section = info.dataset.section;
        const spinner = info.querySelector('.quantity__rules-cart .loading__spinner');
        if (spinner) spinner.classList.remove('hidden');

        fetch(`${info.dataset.url}?variant=${encodeURIComponent(variantId)}&section_id=${encodeURIComponent(section)}`)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            if (html.getElementById(`Quantity-Form-${section}`)) {
              info.updateQuantityRules(section, html);
              // That method works the limit out from the count it is holding
              // and only then takes in the fresh one, which would leave the
              // box a step behind the cart. Working it out once more, now that
              // the new count is in place, keeps the two together.
              if (info.quantityInput && typeof info.setQuantityBoundries === 'function') {
                info.setQuantityBoundries();
              }
            }
            // The limit has just been worked out again against the new cart,
            // so a message about the old one may no longer be true.
            if (this.quantityWithinLimit()) this.clearQuantityError();
          })
          .catch(() => {})
          .finally(() => {
            if (spinner) spinner.classList.add('hidden');
          });
      }
    }
  );
}
